<!-- src/components/rota/RotaToolbar.vue -->
<template>
  <div class="card-panel core-toolbar-panel rota-toolbar">
    <div class="core-toolbar-layout rota-toolbar-row">
      <!-- LEFT: NAVIGATION -->
      <div class="core-toolbar-nav rota-toolbar-nav">
        <button
          class="btn teal lighten-1 waves-effect waves-teal"
          type="button"
          :disabled="busy || hasDateOverride"
          @click="$emit('prev')"
        >
          <i class="material-icons left"> chevron_left </i>

          Prev
        </button>

        <button
          class="btn teal darken-2 waves-effect waves-teal"
          type="button"
          :disabled="busy || hasDateOverride || !canGoNext"
          @click="$emit('next')"
        >
          Next

          <i class="material-icons right"> chevron_right </i>
        </button>
      </div>

      <!-- CENTRE: RANGE LABEL -->
      <div class="core-toolbar-centre rota-toolbar-week">
        <span v-if="hasDateOverride" class="range-title">
          Date range:
          <strong>{{ rangeStart }}</strong>

          <span class="range-arrow"> → </span>

          <strong>{{ rangeEnd }}</strong>
        </span>

        <span v-else class="range-title">
          Week:
          <strong>{{ weekRangeText }}</strong>
        </span>

        <span
          v-if="!canGoNext && !hasDateOverride"
          class="chip core-toolbar-chip red lighten-5 red-text text-darken-2"
        >
          Locked (beyond current week +6)
        </span>

        <span
          v-if="hasDateOverride"
          class="chip core-toolbar-chip blue-grey lighten-5 blue-grey-text text-darken-3"
        >
          Temporary date override active
        </span>
      </div>

      <!-- RIGHT -->
      <div class="core-toolbar-right rota-toolbar-right">
        <!-- PRINT WEEKLY PACK -->
        <button
          v-if="showPrintWeeklyPack"
          class="btn purple darken-2 waves-effect waves-light print-pack-btn"
          type="button"
          :disabled="busy || !effectivePrintWeekEnding"
          :title="
            effectivePrintWeekEnding
              ? `Print weekly pack for W/E ${effectivePrintWeekEnding}`
              : 'No week ending selected'
          "
          @click="printWeeklyPack"
        >
          <i class="material-icons left"> picture_as_pdf </i>

          PRINT WEEKLY PACK
        </button>

        <!-- DATE RANGE PICKERS -->
        <div
          v-if="showDateRangePicker"
          class="core-toolbar-range-panel rota-toolbar-range-panel"
        >
          <div class="core-date-field">
            <label for="rota-range-start"> From date </label>

            <input
              id="rota-range-start"
              v-model="localStartDate"
              type="date"
              :disabled="busy"
            />
          </div>

          <div class="core-date-field">
            <label for="rota-range-end"> End date </label>

            <input
              id="rota-range-end"
              v-model="localEndDate"
              type="date"
              :disabled="busy"
            />
          </div>

          <button
            class="btn blue-grey darken-2 waves-effect waves-light apply-btn"
            type="button"
            :disabled="busy || !canApplyRange"
            @click="applyDateRange"
          >
            Apply
          </button>

          <button
            v-if="hasDateOverride"
            class="btn-flat red-text text-darken-2 clear-btn"
            type="button"
            :disabled="busy"
            @click="clearDateRange"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  weekRangeText: {
    type: String,
    default: "",
  },

  weekEnding: {
    type: String,
    default: "",
  },

  canGoNext: {
    type: Boolean,
    default: true,
  },

  canEdit: {
    type: Boolean,
    default: true,
  },

  busy: {
    type: Boolean,
    default: false,
  },

  showPrintWeeklyPack: {
    type: Boolean,
    default: false,
  },

  showDateRangePicker: {
    type: Boolean,
    default: false,
  },

  rangeStart: {
    type: String,
    default: "",
  },

  rangeEnd: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "prev",
  "next",
  "refresh",
  "date-range-change",
  "date-range-clear",
  "print-weekly-pack",
]);

const localStartDate = ref(props.rangeStart || "");

const localEndDate = ref(props.rangeEnd || "");

const hasDateOverride = computed(() => {
  return Boolean(props.rangeStart && props.rangeEnd);
});

const canApplyRange = computed(() => {
  if (!localStartDate.value || !localEndDate.value) {
    return false;
  }

  return localStartDate.value <= localEndDate.value;
});

const effectivePrintWeekEnding = computed(() => {
  if (hasDateOverride.value && props.rangeEnd) {
    return props.rangeEnd;
  }

  return props.weekEnding || "";
});

watch(
  () => props.rangeStart,

  (value) => {
    localStartDate.value = value || "";
  }
);

watch(
  () => props.rangeEnd,

  (value) => {
    localEndDate.value = value || "";
  }
);

function applyDateRange() {
  if (!canApplyRange.value) {
    return;
  }

  emit("date-range-change", {
    startDate: localStartDate.value,

    endDate: localEndDate.value,
  });
}

function clearDateRange() {
  localStartDate.value = "";

  localEndDate.value = "";

  emit("date-range-clear");
}

function printWeeklyPack() {
  const selectedWeekEnding = effectivePrintWeekEnding.value;

  if (!selectedWeekEnding) {
    return;
  }

  emit("print-weekly-pack", {
    weekEnding: selectedWeekEnding,

    rangeStart: hasDateOverride.value ? props.rangeStart : "",

    rangeEnd: hasDateOverride.value ? props.rangeEnd : "",

    hasDateOverride: hasDateOverride.value,
  });
}
</script>

<style scoped>
.rota-toolbar {
  margin-top: 12px;
}

.rota-toolbar-nav {
  white-space: nowrap;
}

.range-title {
  font-weight: 600;
  white-space: nowrap;
}

.range-arrow {
  display: inline-block;
  margin: 0 4px;
}

.print-pack-btn {
  white-space: nowrap;
}

@media (min-width: 1201px) {
  .rota-toolbar-right,
  .rota-toolbar-range-panel {
    justify-self: end;
  }
}

@media (max-width: 1200px) {
  .rota-toolbar-row {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 14px;
  }

  .rota-toolbar-nav {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    gap: 12px;
  }

  .rota-toolbar-week {
    justify-content: flex-start;
    line-height: 1.35;
  }

  .range-title {
    white-space: normal;
  }

  .rota-toolbar-right {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .print-pack-btn {
    width: 100% !important;
  }

  .rota-toolbar-range-panel {
    width: 100%;
    grid-template-columns: 1fr 1fr;
    justify-self: stretch;
  }

  .apply-btn,
  .clear-btn {
    width: 100% !important;
  }
}

@media (max-width: 600px) {
  .rota-toolbar {
    padding: 10px;
  }

  .rota-toolbar-nav {
    gap: 8px;
  }

  .rota-toolbar-nav .btn {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}
</style>
